// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
/* istanbul ignore file */
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { Checkbox, ListSubheader, type PopoverOrigin } from '@mui/material';
import {
  useCallback,
  useMemo,
  type FC,
  type ChangeEvent,
  type ReactNode,
  useState,
} from 'react';
import { AppIcon, type SvgIconName } from '@shared/ui-kit/components/app-icon';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { isFieldValueEmpty } from '@shared/ui-kit/utils/is-field-value-empty';
import { groupBy } from '@shared/ui-kit/utils';
import { FixedSizeList as List, type ListChildComponentProps } from 'react-window';
import { SearchBar } from '@shared/ui-kit/components';
import { useConst } from '@shared/ui-kit/hooks';
import { useTranslation } from 'react-i18next';
import { isNullOrUndefined, isUndefined } from '@shared/utils';

type SelectStyle = 'white' | 'gray';

export type AvailableOptions = Array<
  | { value: string; label: string; error?: boolean; disabled?: boolean }
  | {
      value: string;
      label: string;
      group: string;
      error?: boolean;
      disabled?: boolean;
    }
  | {
      value: string;
      label: string;
      group: string;
      groupLabel: string;
      error?: boolean;
      disabled?: boolean;
    }
  | string
>;

type RowData = {
  options: AvailableOptions;
  multiple?: boolean;
  grouping?: boolean;
  checkbox?: boolean;
  selected: string | string[] | undefined;
  disabled?: boolean;
  onClick: (value: string) => void;
};

export interface OptionsSelectorProps<T extends string | string[]> {
  optionsAvailable: AvailableOptions;
  onChangeValueSelected: (newOptionSelected: T) => void;
  optionSelected?: T;
  dataTestId?: string;
  'data-testid'?: string;
  defaultText: string;
  selectType: SelectStyle;
  multiple?: T extends string ? false : true;
  selectedAll?: boolean;
  checkbox?: boolean;
  grouping?: boolean;
  searchBar?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  defaultValue?: T;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  collapseSelectedLabelsAt?: number;
  /** When set, shows this icon to the left of the displayed value (closed state). */
  leadingIcon?: SvgIconName;
}

const Row: FC<ListChildComponentProps<RowData>> = ({ index, style, data }) => {
  const option = data.options[index];

  const value = typeof option === 'string' ? option : option.value;

  const error = typeof option === 'string' ? false : option.error;

  const selected =
    typeof data.selected === 'string'
      ? data.selected === value
      : data.selected?.includes(value);

  return (
    <MenuItem
      key={index}
      value={value}
      selected={selected}
      style={style}
      disabled={
        data.disabled || (typeof option === 'object' ? option.disabled : false)
      }
      onClick={() => {
        if (data.disabled) {
          return;
        }

        data.onClick(value);
      }}
      className={classNames(OPTIONS_SELECTOR_MENU_ITEM_CLASS, {
        '!text-negative': error,
      })}
    >
      {data.checkbox && data.multiple ? <Checkbox checked={selected} /> : null}
      {typeof option === 'string' ? option : option?.label}
    </MenuItem>
  );
};

const containsText = (text: string, searchText: string) => {
  const query = searchText.trim().toLowerCase();

  if (!query) {
    return true;
  }

  return text.toLowerCase().includes(query);
};

export const OPTIONS_SELECTOR_MENU_ITEM_CLASS = classNames(
  '!mx-1 !my-0.5 !rounded-lg !border !border-transparent !transition-all',
  'hover:!border-primary hover:!bg-primary/5 hover:!text-title-navy',
  '[&.Mui-focusVisible]:!border-primary [&.Mui-focusVisible]:!bg-primary/5',
  '[&.Mui-selected]:!border-primary/30 [&.Mui-selected]:!bg-primary/10 [&.Mui-selected]:!text-primary',
  '[&.Mui-selected]:hover:!border-primary [&.Mui-selected]:hover:!bg-primary/15',
  '[&.Mui-disabled]:hover:!border-transparent [&.Mui-disabled]:hover:!bg-transparent',
);

export const OptionsSelector = <T extends string | string[]>({
  optionsAvailable,
  onChangeValueSelected,
  optionSelected,
  dataTestId,
  'data-testid': testId,
  defaultText,
  selectType,
  multiple = false as any,
  checkbox = false,
  grouping = false,
  selectedAll = true,
  searchBar = false,
  anchorOrigin,
  defaultValue,
  transformOrigin,
  error,
  disabled,
  required,
  collapseSelectedLabelsAt,
  leadingIcon,
}: OptionsSelectorProps<T>) => {
  const { t } = useTranslation();
  const ITEM_HEIGHT = useConst(35);
  const DISPLAYED_OPTIONS = useConst(8);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);

  const displayedOptions = useMemo(
    () =>
      optionsAvailable?.filter(option =>
        containsText(
          typeof option === 'string' ? option : option.label,
          searchText,
        ),
      ) ?? [],
    [optionsAvailable, searchText],
  );

  const listHeight = useMemo(
    () =>
      displayedOptions.length > DISPLAYED_OPTIONS
        ? ITEM_HEIGHT * DISPLAYED_OPTIONS
        : ITEM_HEIGHT * displayedOptions.length,
    [DISPLAYED_OPTIONS, ITEM_HEIGHT, displayedOptions.length],
  );

  const allValues = useMemo(
    () =>
      displayedOptions?.map(option =>
        typeof option === 'string' ? option : option.value,
      ),
    [displayedOptions],
  );

  const hasAllValues = useMemo(
    () =>
      typeof optionSelected === 'string'
        ? false
        : [...allValues].sort().join(',') ===
          [...(optionSelected ?? [])]?.sort()?.join(','),
    [optionSelected, allValues],
  );

  const groupedOptions = useMemo(() => {
    if (!grouping) return [];
    return Array.from(
      groupBy(
        displayedOptions as Array<{
          value: string;
          label: string;
          group: string;
          groupLabel?: string;
          error?: boolean;
          disabled?: boolean;
        }>,
        'group',
      ),
    );
  }, [displayedOptions, grouping]);

  const selectedGroupValues = useCallback(
    (group: string) => {
      if (!grouping) return false;

      const groupValues = groupedOptions
        .filter(([optionGroup]) => optionGroup === group)
        .flatMap(([, options]) => options.map(option => option.value));

      return (
        groupValues.sort().join(',') ===
        (typeof optionSelected === 'string'
          ? optionSelected
          : (optionSelected as string[])
              .filter(option => groupValues.includes(option))
              .sort()
              .join(','))
      );
    },
    [groupedOptions, grouping, optionSelected],
  );

  const toggleGroup = useCallback(
    (group: string) => {
      const groupValues = groupedOptions
        .filter(([optionGroup]) => optionGroup === group)
        .flatMap(([, options]) => options.map(option => option.value));

      selectedGroupValues(group)
        ? onChangeValueSelected(
            (optionSelected as string[])?.filter(
              option => !groupValues.includes(option),
            ) as any as T,
          )
        : onChangeValueSelected(
            Array.from(
              new Set((optionSelected as string[])?.concat(groupValues)),
            ) as any as T,
          );
    },
    [groupedOptions, onChangeValueSelected, optionSelected, selectedGroupValues],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<T>) => {
      const value = event.target.value as T;

      if (multiple && value.includes(undefined as any)) {
        /* istanbul ignore next */
        onChangeValueSelected(
          typeof value === 'string'
            ? (value.split(',') as T)
            : ((hasAllValues ? [] : allValues) as T),
        );
      } else {
        onChangeValueSelected(
          multiple && typeof value === 'string' ? (value.split(',') as T) : value,
        );
      }
    },
    [allValues, hasAllValues, multiple, onChangeValueSelected],
  );

  const onOptionClick = useCallback(
    (value: string) => {
      if (disabled) {
        return;
      }

      const isString = typeof optionSelected === 'string';
      const isArray = Array.isArray(optionSelected);

      const isSelected = isString
        ? optionSelected === value
        : optionSelected?.includes(value) ?? false;

      if (multiple && isArray) {
        onChangeValueSelected(
          (isSelected
            ? (optionSelected as string[]).filter(option => option !== value)
            : (optionSelected ?? []).concat(value)) as T,
        );
      } else if (multiple && !isArray) {
        // Handle case when optionSelected is undefined and multiple is true
        onChangeValueSelected([value] as T);
      } else {
        onChangeValueSelected(value as T);
        setOpen(false);
      }
    },
    [disabled, multiple, onChangeValueSelected, optionSelected],
  );

  /* istanbul ignore next */
  const onChangeSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  /* istanbul ignore next */
  const onClose = useCallback(() => {
    setSearchText('');
    setOpen(false);
  }, []);

  const isEmpty = isFieldValueEmpty(optionSelected, Boolean(multiple));

  return (
    <FormControl fullWidth size="small" error={error} required={required}>
      <Select
        value={
          optionSelected ?? (multiple ? ([] as unknown as T) : ('' as unknown as T))
        }
        IconComponent={props => (
          <AppIcon
            icon="ArrowDown"
            size="base"
            color="disabled"
            className="mr-2"
            {...props}
          />
        )}
        onChange={handleChange}
        onClose={onClose}
        open={open}
        onOpen={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        onBlur={() => setOpen(false)}
        displayEmpty
        disabled={disabled}
        renderValue={(value: T): ReactNode => {
          const wrapDisplay = (content: ReactNode, placeholder = false) =>
            leadingIcon ? (
              <span className="flex w-full min-w-0 max-w-full items-center gap-2">
                <AppIcon
                  icon={leadingIcon}
                  size="base"
                  color="disabled"
                  className="shrink-0"
                  aria-hidden
                />
                <span
                  className={classNames('min-w-0 flex-1 truncate', {
                    'text-sidebar-text': placeholder,
                    'text-title-navy': !placeholder,
                  })}
                >
                  {content}
                </span>
              </span>
            ) : (
              <span
                className={classNames({
                  'text-sidebar-text': placeholder,
                  'text-title-navy': !placeholder,
                })}
              >
                {content}
              </span>
            );

          if (isNullOrUndefined(value)) {
            return wrapDisplay(defaultText, true);
          }

          const values = typeof value === 'string' ? [value] : (value as string[]);

          // Handle empty string or empty array
          if (values.length === 0 || (values.length === 1 && values[0] === '')) {
            return wrapDisplay(defaultText, true);
          }

          if (
            multiple &&
            !isUndefined(collapseSelectedLabelsAt) &&
            values.length > collapseSelectedLabelsAt
          ) {
            return wrapDisplay(
              t('common.selected-count', {
                ns: 'common',
                count: values.length,
              }),
            );
          }

          const labels = optionsAvailable
            ?.filter(option =>
              values.includes(typeof option === 'object' ? option.value : option),
            )
            .map(option => (typeof option === 'object' ? option.label : option));

          return wrapDisplay(
            Array.from(new Set(labels))?.join(',') || defaultText,
            !labels?.length,
          );
        }}
        data-testid={dataTestId || testId}
        className={classNames({
          'options-selector-empty': !error && !disabled && isEmpty && !required,
          'options-selector-empty-required':
            !error && !disabled && isEmpty && required,
          'options-selector-filled': !error && !disabled && !isEmpty,
          'options-selector-gray': selectType === 'gray',
        })}
        multiple={multiple || false}
        MenuProps={{
          anchorOrigin: anchorOrigin ?? {
            horizontal: 'center',
            vertical: 'bottom',
          },
          transformOrigin: transformOrigin ?? {
            vertical: 'top',
            horizontal: 'center',
          },
          autoFocus: false,
          classes: {
            list: '!bg-page !py-1',
          },
        }}
      >
        {searchBar && (
          <ListSubheader className="!bg-page !py-2">
            <SearchBar
              value={searchText}
              onChangeValue={onChangeSearch}
              placeholder={t('common.search', { ns: 'common' })}
              autoFocus
              disabled={disabled}
              onKeyDown={e => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
        )}
        {multiple && selectedAll ? (
          <MenuItem
            className={OPTIONS_SELECTOR_MENU_ITEM_CLASS}
            style={{ height: ITEM_HEIGHT }}
          >
            <Checkbox checked={hasAllValues} />
            <span>{t('common.select-all', { ns: 'common' })}</span>
          </MenuItem>
        ) : null}
        {!multiple && optionSelected && (
          <MenuItem value={optionSelected as string} sx={{ display: 'none' }}>
            {optionSelected}
          </MenuItem>
        )}
        {
          /** @ts-ignore react 18 update */ !grouping && (
            <List
              itemCount={displayedOptions.length}
              width="100%"
              height={listHeight}
              itemSize={ITEM_HEIGHT}
              innerElementType="ul"
              outerElementType="li"
              className="!overflow-x-hidden"
              itemData={{
                options: displayedOptions,
                multiple,
                grouping,
                checkbox,
                selected: optionSelected,
                disabled,
                onClick: onOptionClick,
              }}
            >
              {/** @ts-ignore react 18 update */}
              {Row}
            </List>
          )
        }
        {grouping &&
          groupedOptions.map(([group, options], groupId) => [
            <ListSubheader className="!cursor-default !truncate !bg-inherit !font-poppins !text-sm !font-bold !leading-8 !text-border-dark">
              {checkbox && (
                <Checkbox
                  checked={selectedGroupValues(group)}
                  onChange={() => toggleGroup(group)}
                />
              )}
              <span>{options?.[0]?.groupLabel || group}</span>
            </ListSubheader>,
            options.map((option, id) => (
              <MenuItem
                key={`option-group-${groupId}-${id}`}
                value={option.value}
                className={classNames(OPTIONS_SELECTOR_MENU_ITEM_CLASS, {
                  '!text-negative': option.error,
                })}
                disabled={option.disabled}
              >
                {checkbox && multiple ? (
                  <Checkbox
                    checked={
                      Array.isArray(optionSelected) &&
                      optionSelected.includes(option.value)
                    }
                  />
                ) : null}
                {option.label}
              </MenuItem>
            )),
          ])}
      </Select>
    </FormControl>
  );
};
